import loadable from '@loadable/component';
import { ThemeProvider } from '@mui/material';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React from 'react';
import { Navigate, Route, createBrowserRouter, createRoutesFromElements, useLocation } from 'react-router-dom';

import { ApiSDKContextProvider } from '../contexts/apiSdkContext';
import { useAuthContext } from '../contexts/authContext';
import { TListDailyPlansPageProps } from '../pages/daily-plans/list';
import { TListGroupsPageProps } from '../pages/groups/list';
import { TSettingsPageProps } from '../pages/settings/list';
import { theme } from '../theme';

const DashboardPage: React.ComponentType = loadable(() => import('../pages/dashboard'));
const ScrollToTop: React.ComponentType = loadable(() => import('../components/layout/scroll-to-top'));
const Loading: React.ComponentType = loadable(() => import('../components/loading'));

const EditAreaPage: React.ComponentType = loadable(() => import('../pages/areas/edit'));
const EditCompanyPage: React.ComponentType = loadable(() => import('../pages/companies/edit'));
const EditClientPage: React.ComponentType = loadable(() => import('../pages/clients/edit'));
const EditInputWebhookPage: React.ComponentType = loadable(() => import('../pages/input-webhooks/edit'));
const EditDevicePage: React.ComponentType = loadable(() => import('../pages/devices/edit'));
const EditDeviceTypesPage: React.ComponentType = loadable(() => import('../pages/device-types/edit'));
const EditGatewaysPage: React.ComponentType = loadable(() => import('../pages/gateways/edit'));
const EditGroupsPage: React.ComponentType = loadable(() => import('../pages/groups/edit'));
const EditUserPage: React.ComponentType = loadable(() => import('../pages/users/edit'));
const UserChangePasswordPage: React.ComponentType = loadable(() => import('../pages/users/change-password'));
const EditPassportizationFieldPage: React.ComponentType = loadable(
  () => import('../pages/passportization-fields/edit')
);
const EditPlanPage: React.ComponentType = loadable(() => import('../pages/daily-plans/edit'));
const EditReportPage: React.ComponentType = loadable(() => import('../pages/reports/edit'));
const EditRolePage: React.ComponentType = loadable(() => import('../pages/roles/edit'));
const EditScenePage: React.ComponentType = loadable(() => import('../pages/scenes/edit'));

const ExportDevicesPage: React.ComponentType = loadable(() => import('../pages/devices/export'));
const ImportDevicesPage: React.ComponentType = loadable(() => import('../pages/devices/import'));
const ConfigDevicePage: React.ComponentType = loadable(() => import('../pages/devices/configuration'));
const Layout: React.ComponentType = loadable(() => import('../pages/layout'));
const LoginLayout: React.ComponentType = loadable(() => import('../pages/layout-login'));

const ListAccessTokens: React.ComponentType = loadable(() => import('../pages/access-tokens/list'));
const ListAreasPage: React.ComponentType = loadable(() => import('../pages/areas/list'));
const ListConfigsPage: React.ComponentType = loadable(() => import('../pages/configs/list'));
const ListClientsPage: React.ComponentType = loadable(() => import('../pages/clients/list'));
const ListInputWebhooksPage: React.ComponentType = loadable(() => import('../pages/input-webhooks/list'));
const ListCompaniesPage: React.ComponentType = loadable(() => import('../pages/companies/list'));
const ListDailyPlansPage: React.ComponentType<TListDailyPlansPageProps> = loadable(
  () => import('../pages/daily-plans/list')
);
const ListDevicesAllPage: React.ComponentType = loadable(() => import('../pages/devices/list-all'));
const ListDevicesPage: React.ComponentType = loadable(() => import('../pages/devices/list'));
const ListErrorsPage: React.ComponentType = loadable(() => import('../pages/errors/list'));
const ListMetricsPage: React.ComponentType = loadable(() => import('../pages/metrics/list'));
const ListStatesPage: React.ComponentType = loadable(() => import('../pages/states/list'));
const ListActionsPage: React.ComponentType = loadable(() => import('../pages/actions/list'));
const ListEventsPage: React.ComponentType = loadable(() => import('../pages/events/list'));
const ListDeviceTypesPage: React.ComponentType = loadable(() => import('../pages/device-types/list'));
const ListDeviceProfilesPage: React.ComponentType = loadable(() => import('../pages/device-profiles/list'));

const ListFlowsPage: React.ComponentType = loadable(() => import('../pages/flows/list'));
const ListFlowActionsPage: React.ComponentType = loadable(() => import('../pages/flow-actions/list'));
const ListFlowTriggersPage: React.ComponentType = loadable(() => import('../pages/flow-triggers/list'));

const ListGatewaysPage: React.ComponentType = loadable(() => import('../pages/gateways/list'));
const ListGroupsPage: React.ComponentType<TListGroupsPageProps> = loadable(() => import('../pages/groups/list'));
const ListPassportizationTemplatesPage: React.ComponentType = loadable(
  () => import('../pages/passportization-templates/list')
);
const ListPassportizationFieldsPage: React.ComponentType = loadable(
  () => import('../pages/passportization-fields/list')
);
const ListRefreshTokens: React.ComponentType = loadable(() => import('../pages/refresh-tokens/list'));
const ListResourcesPage: React.ComponentType = loadable(() => import('../pages/resources/list'));
const ListReportsPage: React.ComponentType = loadable(() => import('../pages/reports/list'));
const ListRolesPage: React.ComponentType = loadable(() => import('../pages/roles/list'));
const ListScenesPage: React.ComponentType = loadable(() => import('../pages/scenes/list'));
const ListUsersPage: React.ComponentType = loadable(() => import('../pages/users/list'));
const ListLoginLogsPage: React.ComponentType = loadable(() => import('../pages/login-logs/list'));

const LoginPage: React.ComponentType = loadable(() => import('../pages/login/login-page'));
const ForgotPasswordPage: React.ComponentType = loadable(() => import('../pages/forgot-password/forgot-password-page'));
const ResetPasswordPage: React.ComponentType = loadable(() => import('../pages/reset-password/reset-password-page'));

const NotFoundPage: React.ComponentType = loadable(() => import('../pages/not-found'));

const SettingsPage: React.ComponentType<TSettingsPageProps> = loadable(() => import('../pages/settings/list'));

const UserDetailPage: React.ComponentType = loadable(() => import('../pages/users/show'));

const ShowAccessTokenPage: React.ComponentType = loadable(() => import('../pages/access-tokens/show'));
const ShowActionPage: React.ComponentType = loadable(() => import('../pages/actions/show'));
const ShowAreaPage: React.ComponentType = loadable(() => import('../pages/areas/show'));
const ShowCompanyPage: React.ComponentType = loadable(() => import('../pages/companies/show'));
const ShowConfigPage: React.ComponentType = loadable(() => import('../pages/configs/show'));
const ShowClientPage: React.ComponentType = loadable(() => import('../pages/clients/show'));
const ShowInputWebhookPage: React.ComponentType = loadable(() => import('../pages/input-webhooks/show'));
const ShowDevicePage: React.ComponentType = loadable(() => import('../pages/devices/show'));
const ShowDeviceTypesPage: React.ComponentType = loadable(() => import('../pages/device-types/show'));
const ShowDeviceProfilesPage: React.ComponentType = loadable(() => import('../pages/device-profiles/show'));
const ShowDeviceProfileActionPage: React.ComponentType = loadable(() => import('../pages/device-profile-actions/show'));
const ShowDeviceProfileConfigPage: React.ComponentType = loadable(() => import('../pages/device-profile-configs/show'));
const ShowDeviceProfileErrorPage: React.ComponentType = loadable(() => import('../pages/device-profile-errors/show'));
const ShowDeviceProfileEventPage: React.ComponentType = loadable(() => import('../pages/device-profile-events/show'));
const ShowDeviceProfileMetricPage: React.ComponentType = loadable(() => import('../pages/device-profile-metrics/show'));
const ShowDeviceProfileStatePage: React.ComponentType = loadable(() => import('../pages/device-profile-states/show'));
const ShowErrorPage: React.ComponentType = loadable(() => import('../pages/errors/show'));
const ShowEventPage: React.ComponentType = loadable(() => import('../pages/events/show'));
const ShowGatewaysPage: React.ComponentType = loadable(() => import('../pages/gateways/show'));
const ShowGroupPage: React.ComponentType = loadable(() => import('../pages/groups/show'));
const ShowMainDailyPlan: React.ComponentType = loadable(() => import('../pages/daily-plans/show'));
const ShowMapPage: React.ComponentType = loadable(() => import('../pages/map/show'));
const ShowMetricsPage: React.ComponentType = loadable(() => import('../pages/metrics/show'));
const ShowPassportizationFieldPage: React.ComponentType = loadable(
  () => import('../pages/passportization-fields/show')
);
const ShowPassportizationTemplatePage: React.ComponentType = loadable(
  () => import('../pages/passportization-templates/show')
);
const ShowRefreshTokenPage: React.ComponentType = loadable(() => import('../pages/refresh-tokens/show'));
const ShowReportPage: React.ComponentType = loadable(() => import('../pages/reports/show'));
const ShowResourcePage: React.ComponentType = loadable(() => import('../pages/resources/show'));
const ShowRolePage: React.ComponentType = loadable(() => import('../pages/roles/show'));
const ShowScenePage: React.ComponentType = loadable(() => import('../pages/scenes/show'));
const ShowStatePage: React.ComponentType = loadable(() => import('../pages/states/show'));
const ShowLoginLog: React.ComponentType = loadable(() => import('../pages/login-logs/show'));
const ShowDeviceImport: React.ComponentType = loadable(() => import('../pages/devices/show-import'));
const ShowDeviceExport: React.ComponentType = loadable(() => import('../pages/devices/show-export'));

/* PUBLIC-ONLY */
const PublicOnly: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoggedIn, authDegraded } = useAuthContext();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const redirectUrl = params.get('redirectUrl');

  if (isLoggedIn && !authDegraded) {
    return <Navigate to={redirectUrl || '/dashboard'} replace />;
  }
  return <>{children}</>;
};

/* PROTECTED */
const Protected: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoggedIn, authReady, authDegraded } = useAuthContext();
  const location = useLocation();

  if (!authReady) {
    return <Loading />;
  }

  if (authDegraded) {
    return <>{children}</>;
  }

  if (!isLoggedIn) {
    const redirectUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirectUrl=${redirectUrl}`} replace state={{ from: location }} />;
  }

  return <>{children}</>;
};

const AppShell: React.FC = () => (
  <>
    <Layout />
    <ScrollToTop />
    <ReactQueryDevtools position="bottom-right" initialIsOpen={false} />
  </>
);

export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter(
  createRoutesFromElements(
    <>
      {/* PUBLIC */}
      <Route element={<LoginLayout />}>
        <Route
          path="/login"
          element={
            <PublicOnly>
              <LoginPage />
            </PublicOnly>
          }
        />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:email" element={<ResetPasswordPage />} />
      </Route>

      {/* PRIVATE */}
      <Route
        element={
          <Protected>
            <ApiSDKContextProvider>
              <ThemeProvider theme={theme}>
                <AppShell />
              </ThemeProvider>
            </ApiSDKContextProvider>
          </Protected>
        }
      >
        {/* PRIVATE ROUTES */}
        <Route index element={<DashboardPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/areas" element={<ListAreasPage />} />
        <Route path="/areas/create" element={<EditAreaPage />} />
        <Route path="areas/:areaId" element={<ShowAreaPage />} />
        <Route path="areas/:areaId/suntimes" element={<ShowAreaPage />} />
        <Route path="areas/:areaId/edit" element={<EditAreaPage />} />
        <Route path="daily-plans" element={<ListDailyPlansPage filter="all" />} />
        <Route path="daily-plans/main" element={<ListDailyPlansPage filter="main" />} />
        <Route path="daily-plans/extra" element={<ListDailyPlansPage filter="extra" />} />
        <Route path="daily-plans/main/create" element={<EditPlanPage />} />
        <Route path="daily-plans/extra/create" element={<EditPlanPage />} />
        <Route path="daily-plans/:planId" element={<ShowMainDailyPlan />} />
        <Route path="daily-plans/:planId/edit" element={<EditPlanPage />} />
        <Route path="devices" element={<ListDevicesAllPage />} />
        <Route path="devices/:deviceTypeId" element={<ListDevicesPage />} />
        <Route path="devices/:deviceTypeId/create" element={<EditDevicePage />} />
        <Route path="devices/:deviceTypeId/create/:cloneDeviceId" element={<EditDevicePage />} />
        <Route path="devices/:deviceTypeId/:deviceId" element={<ShowDevicePage />} />
        <Route path="devices/:deviceTypeId/:deviceId/passportization" element={<ShowDevicePage />} />
        <Route path="devices/:deviceTypeId/:deviceId/config" element={<ShowDevicePage />} />
        <Route path="devices/:deviceTypeId/:deviceId/statistics" element={<ShowDevicePage />} />
        <Route path="devices/:deviceTypeId/:deviceId/errors" element={<ShowDevicePage />} />
        <Route path="devices/:deviceTypeId/:deviceId/frames" element={<ShowDevicePage />} />
        <Route path="devices/:deviceTypeId/:deviceId/actions" element={<ShowDevicePage />} />
        <Route path="devices/:deviceTypeId/:deviceId/edit" element={<EditDevicePage />} />
        <Route path="devices/:deviceTypeId/:deviceId/configuration" element={<ConfigDevicePage />} />
        <Route path="gateways" element={<ListGatewaysPage />} />
        <Route path="gateways/create" element={<EditGatewaysPage />} />
        <Route path="gateways/create/:cloneGatewayId" element={<EditGatewaysPage />} />
        <Route path="gateways/:gatewayId" element={<ShowGatewaysPage />} />
        <Route path="gateways/:gatewayId/frames" element={<ShowGatewaysPage />} />
        <Route path="gateways/:gatewayId/edit" element={<EditGatewaysPage />} />
        <Route path="groups" element={<ListGroupsPage filter="all" />} />
        <Route path="groups/with-plan" element={<ListGroupsPage filter="with-plan" />} />
        <Route path="groups/without-plan" element={<ListGroupsPage filter="without-plan" />} />
        <Route path="groups/create" element={<EditGroupsPage />} />
        <Route path="groups/:groupId" element={<ShowGroupPage />} />
        <Route path="groups/:groupId/actions" element={<ShowGroupPage />} />
        <Route path="groups/:groupId/device-list" element={<ShowGroupPage />} />
        <Route path="groups/:groupId/device-map" element={<ShowGroupPage />} />
        <Route path="groups/:groupId/edit" element={<EditGroupsPage />} />
        <Route path="scenes" element={<ListScenesPage />} />
        <Route path="scenes/create" element={<EditScenePage />} />
        <Route path="scenes/:sceneId" element={<ShowScenePage />} />
        <Route path="scenes/:sceneId/edit" element={<EditScenePage />} />
        <Route path="map" element={<ShowMapPage />} />
        <Route path="settings" element={<SettingsPage component={<ListCompaniesPage />} />} />
        <Route path="settings/access-tokens" element={<SettingsPage component={<ListAccessTokens />} />} />
        <Route
          path="settings/access-tokens/:accessTokenId"
          element={<SettingsPage component={<ShowAccessTokenPage />} />}
        />
        <Route path="settings/flows" element={<SettingsPage component={<ListFlowsPage />} />} />
        <Route path="settings/flow-actions" element={<SettingsPage component={<ListFlowActionsPage />} />} />
        <Route path="settings/flow-triggers" element={<SettingsPage component={<ListFlowTriggersPage />} />} />
        <Route path="settings/metrics" element={<SettingsPage component={<ListMetricsPage />} />} />
        <Route path="settings/metrics/:metricId" element={<SettingsPage component={<ShowMetricsPage />} />} />
        <Route path="settings/states" element={<SettingsPage component={<ListStatesPage />} />} />
        <Route path="settings/states/:stateId" element={<SettingsPage component={<ShowStatePage />} />} />
        <Route path="settings/actions" element={<SettingsPage component={<ListActionsPage />} />} />
        <Route path="settings/actions/:actionId" element={<SettingsPage component={<ShowActionPage />} />} />
        <Route path="settings/events" element={<SettingsPage component={<ListEventsPage />} />} />
        <Route path="settings/events/:eventId" element={<SettingsPage component={<ShowEventPage />} />} />
        <Route path="settings/configs" element={<SettingsPage component={<ListConfigsPage />} />} />
        <Route path="settings/configs/:configId" element={<SettingsPage component={<ShowConfigPage />} />} />
        <Route path="settings/errors" element={<SettingsPage component={<ListErrorsPage />} />} />
        <Route path="settings/errors/:errorId" element={<SettingsPage component={<ShowErrorPage />} />} />
        <Route path="settings/device-profiles" element={<SettingsPage component={<ListDeviceProfilesPage />} />} />
        <Route
          path="settings/device-profiles/:deviceProfileId"
          element={<SettingsPage component={<ShowDeviceProfilesPage />} />}
        />
        <Route
          path="settings/device-profile-actions/:deviceProfileActionId"
          element={<SettingsPage component={<ShowDeviceProfileActionPage />} />}
        />
        <Route
          path="settings/device-profile-configs/:deviceProfileConfigId"
          element={<SettingsPage component={<ShowDeviceProfileConfigPage />} />}
        />
        <Route
          path="settings/device-profile-errors/:deviceProfileErrorId"
          element={<SettingsPage component={<ShowDeviceProfileErrorPage />} />}
        />
        <Route
          path="settings/device-profile-events/:deviceProfileEventId"
          element={<SettingsPage component={<ShowDeviceProfileEventPage />} />}
        />
        <Route
          path="settings/device-profile-metrics/:deviceProfileMetricId"
          element={<SettingsPage component={<ShowDeviceProfileMetricPage />} />}
        />
        <Route
          path="settings/device-profile-states/:deviceProfileStateId"
          element={<SettingsPage component={<ShowDeviceProfileStatePage />} />}
        />
        <Route path="settings/device-types" element={<SettingsPage component={<ListDeviceTypesPage />} />} />
        <Route
          path="settings/device-types/:deviceTypeId"
          element={<SettingsPage component={<ShowDeviceTypesPage />} />}
        />
        <Route
          path="settings/device-types/:deviceTypeId/edit"
          element={<SettingsPage component={<EditDeviceTypesPage />} />}
        />
        <Route path="settings/refresh-tokens" element={<SettingsPage component={<ListRefreshTokens />} />} />
        <Route
          path="settings/refresh-tokens/:refreshTokenId"
          element={<SettingsPage component={<ShowRefreshTokenPage />} />}
        />
        <Route path="settings/resources" element={<SettingsPage component={<ListResourcesPage />} />} />
        <Route path="settings/resources/:resourceId" element={<SettingsPage component={<ShowResourcePage />} />} />
        <Route path="settings/roles" element={<SettingsPage component={<ListRolesPage />} />} />
        <Route path="settings/roles/create" element={<SettingsPage component={<EditRolePage />} />} />
        <Route path="settings/roles/:roleId" element={<SettingsPage component={<ShowRolePage />} />} />
        <Route path="settings/roles/:roleId/edit" element={<SettingsPage component={<EditRolePage />} />} />
        <Route path="settings/clients" element={<SettingsPage component={<ListClientsPage />} />} />
        <Route path="settings/clients/create" element={<SettingsPage component={<EditClientPage />} />} />
        <Route path="settings/clients/:clientId" element={<SettingsPage component={<ShowClientPage />} />} />
        <Route path="settings/clients/:clientId/edit" element={<SettingsPage component={<EditClientPage />} />} />
        <Route path="settings/input-webhooks" element={<SettingsPage component={<ListInputWebhooksPage />} />} />
        <Route path="settings/input-webhooks/create" element={<SettingsPage component={<EditInputWebhookPage />} />} />
        <Route
          path="settings/input-webhooks/:inputWebhookId"
          element={<SettingsPage component={<ShowInputWebhookPage />} />}
        />
        <Route
          path="settings/input-webhooks/:inputWebhookId/edit"
          element={<SettingsPage component={<EditInputWebhookPage />} />}
        />
        <Route path="settings/companies" element={<SettingsPage component={<ListCompaniesPage />} />} />
        <Route path="settings/companies/create" element={<SettingsPage component={<EditCompanyPage />} />} />
        <Route path="settings/companies/:companyId" element={<SettingsPage component={<ShowCompanyPage />} />} />
        <Route path="settings/companies/:companyId/edit" element={<SettingsPage component={<EditCompanyPage />} />} />
        <Route path="settings/users" element={<SettingsPage component={<ListUsersPage />} />} />
        <Route path="settings/users/create" element={<SettingsPage component={<EditUserPage />} />} />
        <Route path="settings/users/:userId" element={<SettingsPage component={<UserDetailPage />} />} />
        <Route path="settings/users/:userId/login-logs" element={<SettingsPage component={<UserDetailPage />} />} />
        <Route path="settings/users/:userId/edit" element={<SettingsPage component={<EditUserPage />} />} />
        <Route
          path="settings/users/:userId/change-password"
          element={<SettingsPage component={<UserChangePasswordPage />} />}
        />
        <Route path="settings/login-logs" element={<SettingsPage component={<ListLoginLogsPage />} />} />
        <Route path="settings/login-logs/:loginLogId" element={<SettingsPage component={<ShowLoginLog />} />} />
        <Route
          path="settings/passportization-templates"
          element={<SettingsPage component={<ListPassportizationTemplatesPage />} />}
        />
        <Route
          path="settings/passportization-templates/:passportizationTemplateId"
          element={<SettingsPage component={<ShowPassportizationTemplatePage />} />}
        />
        <Route
          path="settings/passportization-fields"
          element={<SettingsPage component={<ListPassportizationFieldsPage />} />}
        />
        <Route
          path="settings/passportization-fields/:passportizationFieldId"
          element={<SettingsPage component={<ShowPassportizationFieldPage />} />}
        />
        <Route
          path="settings/passportization-fields/:passportizationFieldId/edit"
          element={<SettingsPage component={<EditPassportizationFieldPage />} />}
        />
        <Route path="settings/export-devices" element={<SettingsPage component={<ExportDevicesPage />} />} />
        <Route path="settings/export-devices/:exportId" element={<SettingsPage component={<ShowDeviceExport />} />} />
        <Route path="settings/import-devices" element={<SettingsPage component={<ImportDevicesPage />} />} />
        <Route path="settings/import-devices/:importId" element={<SettingsPage component={<ShowDeviceImport />} />} />
        <Route path="reports" element={<ListReportsPage />} />
        <Route path="reports/:reportId" element={<ShowReportPage />} />
        <Route path="reports/:reportId/history" element={<ShowReportPage />} />
        <Route path="reports/:reportId/edit" element={<EditReportPage />} />
        <Route path="reports/create" element={<EditReportPage />} />
        <Route path="*" element={<NotFoundPage />} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/* FALLBACK */}
      <Route path="*" element={<NotFoundPage />} />
    </>
  )
);
